import React, { useCallback, useEffect, useState } from 'react';
import { Mail, Loader2, RefreshCcw, Eye, Save, Sparkles } from 'lucide-react';
import api from '@/lib/axios';

export default function MarketplaceEmailTemplatesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { setItems((await api.get('/admin/marketplace/email-templates')).data.items || []); }
    catch (e) { setErr(e.response?.data?.detail || 'Falha a carregar.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seed = async () => { await api.post('/admin/marketplace/email-templates/seed'); load(); };
  const save = async () => {
    if (!sel) return;
    await api.put(`/admin/marketplace/email-templates/${sel.key}`, { assunto: sel.assunto, html: sel.html, ativo: sel.ativo });
    load();
  };
  const doPreview = async () => {
    if (!sel) return;
    const vars = {}; (sel.variaveis || []).forEach((v) => { vars[v] = `[${v}]`; });
    setPreview((await api.post(`/admin/marketplace/email-templates/${sel.key}/preview`, { exemplo: vars })).data);
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="email-templates-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
          <Mail className="h-5 w-5 text-emerald-600" /> Centro de templates de email
        </h2>
        <div className="flex gap-2">
          <button type="button" onClick={seed} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600" data-testid="email-templates-seed">
            <Sparkles className="h-3.5 w-3.5" /> Semear modelos
          </button>
          <button type="button" onClick={load} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600">
            <RefreshCcw className="h-3.5 w-3.5" /> Atualizar
          </button>
        </div>
      </div>
      {err && <p className="mt-2 text-xs font-semibold text-red-600">{err}</p>}
      {loading ? (
        <p className="mt-4 flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> A carregar...</p>
      ) : (
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          <ul className="space-y-1.5 md:col-span-1" data-testid="email-templates-list">
            {items.length === 0 && <li className="text-xs text-slate-500">Sem templates. Use "Semear modelos".</li>}
            {items.map((t) => (
              <li key={t.key}>
                <button type="button" onClick={() => { setSel({ ...t }); setPreview(null); }}
                  className={`w-full rounded-lg border px-3 py-2 text-left text-xs ${sel?.key === t.key ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'}`}
                  data-testid={`email-template-item-${t.key}`}>
                  <span className="font-semibold text-slate-800">{t.nome}</span>
                  <span className="block text-[10px] text-slate-500">{t.key} · {t.ativo ? 'ativo' : 'inativo'}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="md:col-span-2">
            {sel ? (
              <div className="space-y-2">
                <input value={sel.assunto || ''} onChange={(e) => setSel({ ...sel, assunto: e.target.value })}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs" placeholder="Assunto" data-testid="email-template-subject" />
                <textarea value={sel.html || ''} onChange={(e) => setSel({ ...sel, html: e.target.value })}
                  rows={8} className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-[11px]" placeholder="HTML" data-testid="email-template-html" />
                <p className="text-[10px] text-slate-500">Variáveis: {(sel.variaveis || []).map((v) => `{{${v}}}`).join(', ') || '—'}</p>
                <div className="flex gap-2">
                  <button type="button" onClick={save} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white" data-testid="email-template-save">
                    <Save className="h-3.5 w-3.5" /> Guardar
                  </button>
                  <button type="button" onClick={doPreview} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600" data-testid="email-template-preview-btn">
                    <Eye className="h-3.5 w-3.5" /> Pré-visualizar (não envia)
                  </button>
                </div>
                {preview && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="email-template-preview">
                    <p className="text-[11px] font-bold text-slate-700">{preview.assunto}</p>
                    <div className="mt-1 border-t border-slate-200 pt-2 text-[11px] text-slate-600" dangerouslySetInnerHTML={{ __html: preview.html }} />
                    <p className="mt-1 text-[10px] text-emerald-600">{preview.nota}</p>
                  </div>
                )}
              </div>
            ) : <p className="text-xs text-slate-500">Selecione um template para editar.</p>}
          </div>
        </div>
      )}
    </section>
  );
}
